import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import jsPDF from 'jspdf';
import { apiFetch } from '../lib/apiClient';

interface DeploymentTarget { subscriptionId: string; resourceGroup: string; location: string; }
interface PlaybookInventoryItem {
  id: string;
  customerId: string;
  subscriptionId: string;
  resourceGroup: string;
  workflowName: string;
  resourceId: string;
  location?: string;
  state?: string;
  triggerName?: string | null;
  triggerKind?: string | null;
  triggerType?: string | null;
  entityKind?: string | null;
  description?: string;
  aiDescription?: string;
  descriptionSource?: string;
  connectors?: string[];
  actionsSummary?: Array<{ title?: string; type?: string; risk?: string; approvalRequired?: boolean; connector?: string; purpose?: string }>;
  rawSummary?: Record<string, unknown>;
  updatedAt?: string;
  discoveredAt?: string;
}

function downloadText(filename: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
function badge(value?: string | null) {
  const v = String(value || '').toLowerCase();
  if (v.includes('enabled') || v.includes('http') || v.includes('sentinel')) return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  if (v.includes('disabled') || v.includes('recurrence') || v.includes('other')) return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300';
  return 'border-border bg-muted text-muted-foreground';
}
function formatDate(value?: string | null) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}
function csvEscape(value: unknown): string {
  const s = String(value ?? '');
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function ResponseOpsPlaybookDiscoveryPanel({ customerId, target, setTarget, subscriptions, resourceGroups, regions, canDiscover, canExport }: { customerId: string; target: DeploymentTarget; setTarget: (target: DeploymentTarget) => void; subscriptions: string[]; resourceGroups: string[]; regions: string[]; canDiscover: boolean; canExport: boolean }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<PlaybookInventoryItem | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const inventoryQ = useQuery({
    queryKey: ['responseops-playbook-inventory', customerId],
    queryFn: () => apiFetch<{ ok: boolean; items: PlaybookInventoryItem[] }>(`/responseops/playbook-inventory?customerId=${encodeURIComponent(customerId)}`),
    enabled: !!customerId,
  });
  const discoverM = useMutation({
    mutationFn: () => apiFetch<{ ok: boolean; total: number; items: PlaybookInventoryItem[]; ai?: any; scope?: string; fallbackReason?: string }>(`/responseops/playbook-discovery?customerId=${encodeURIComponent(customerId)}&subscriptionId=${encodeURIComponent(target.subscriptionId || '')}&resourceGroup=${encodeURIComponent(target.resourceGroup || '')}&location=${encodeURIComponent(target.location || '')}`),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['responseops-playbook-inventory', customerId] });
      const scopeText = r.scope === 'resource_group_fallback'
        ? `from resource group ${target.resourceGroup || '-'} after subscription-scope fallback`
        : `across subscription ${target.subscriptionId || '-'}`;
      const aiText = r.ai?.used
        ? ` AI descriptions generated${Number.isFinite(Number(r.ai.count)) ? ` for ${Number(r.ai.count)} playbook${Number(r.ai.count) === 1 ? '' : 's'}` : ''}${r.ai.fallbackCount ? `; deterministic descriptions kept for ${r.ai.fallbackCount}.` : '.'}`
        : r.ai?.reason ? ` Deterministic descriptions used: ${r.ai.reason}` : '';
      setMessage(`Discovered ${r.total} Logic App playbook${r.total === 1 ? '' : 's'} ${scopeText}.${aiText}`);
      setSelected(r.items?.[0] ?? null);
    },
    onError: (e) => setMessage(e instanceof Error ? e.message : String(e)),
  });
  const exportM = useMutation({
    mutationFn: () => apiFetch<{ ok: boolean; csv: string; filename: string; rows?: PlaybookInventoryItem[] }>('/responseops/playbook-inventory/export', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, format: 'csv' }),
    }),
    onSuccess: (r) => downloadText(r.filename || 'responseops-playbook-inventory.csv', r.csv, 'text/csv'),
    onError: (e) => setMessage(e instanceof Error ? e.message : String(e)),
  });

  const items = inventoryQ.data?.items ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => [item.workflowName, item.resourceGroup, item.subscriptionId, item.state, item.triggerType, item.resourceId, item.aiDescription, item.description, ...(item.connectors ?? [])]
      .some((value) => String(value ?? '').toLowerCase().includes(needle)));
  }, [items, q]);
  const active = selected ?? filtered[0] ?? null;
  const byRg = useMemo(() => {
    const out = new Map<string, number>();
    for (const item of items) out.set(item.resourceGroup || '-', (out.get(item.resourceGroup || '-') ?? 0) + 1);
    return out;
  }, [items]);

  async function exportPdf() {
    if (canExport) {
      try {
        await apiFetch('/responseops/playbook-inventory/export', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId, format: 'pdf' }),
        });
      } catch {
        // PDF is generated client-side; keep export available even if audit callback fails.
      }
    }
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = 15;
    const add = (text: string, size = 9, bold = false) => {
      if (y > 278) { pdf.addPage(); y = 15; }
      pdf.setFont('helvetica', bold ? 'bold' : 'normal');
      pdf.setFontSize(size);
      for (const line of pdf.splitTextToSize(text || ' ', 180)) {
        if (y > 278) { pdf.addPage(); y = 15; }
        pdf.text(String(line), 14, y);
        y += size >= 13 ? 7 : 5;
      }
    };
    add('KloudSecure ResponseOps Playbook Discovery', 16, true);
    add(`Generated ${new Date().toLocaleString()} · Customer ${customerId} · Subscription ${target.subscriptionId || '-'}`);
    add(`Total playbooks: ${items.length} · Resource groups: ${byRg.size}`, 10, true);
    filtered.slice(0, 80).forEach((item, index) => {
      y += 2;
      add(`${index + 1}. ${item.workflowName}`, 12, true);
      add(`RG: ${item.resourceGroup} · State: ${item.state || '-'} · Trigger: ${item.triggerType || '-'} · Location: ${item.location || '-'}`);
      add(`Connectors: ${(item.connectors ?? []).join(', ') || '-'}`);
      add(item.aiDescription || item.description || '-');
      add(`Resource ID: ${item.resourceId}`);
    });
    pdf.save(`responseops-playbook-inventory-${customerId}.pdf`);
  }

  const csvPreview = useMemo(() => {
    const headers = ['SubscriptionId','ResourceGroup','LogicAppName','State','TriggerType','Connectors','Description','ResourceId'];
    return [headers.join(','), ...filtered.map((item) => [item.subscriptionId, item.resourceGroup, item.workflowName, item.state, item.triggerType, (item.connectors ?? []).join(';'), item.aiDescription || item.description, item.resourceId].map(csvEscape).join(','))].join('\n');
  }, [filtered]);

  return <div className="space-y-5">
    <div className="rounded-2xl border bg-card p-5">
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold">Subscription Playbook Discovery</h3>
          <p className="text-sm text-muted-foreground max-w-4xl mt-1">Discover Microsoft.Logic/workflows across the selected customer subscription, generate customer-safe AI descriptions, and feed Playbook Access Master so execution can be governed per tenant/playbook/role.</p>
          <div className="mt-2 text-xs text-muted-foreground">Discovery scope is subscription-wide. Resource group is shown for deployment/API-connection context only.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button disabled={!canDiscover || discoverM.isPending || !target.subscriptionId} onClick={() => discoverM.mutate()} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold disabled:opacity-50">{discoverM.isPending ? 'Discovering…' : 'Discover Playbooks'}</button>
          {canExport && <button disabled={exportM.isPending || !items.length} onClick={() => exportM.mutate()} className="rounded-xl border px-4 py-2 text-sm font-bold disabled:opacity-50">Export CSV</button>}
          {canExport && <button disabled={!items.length} onClick={exportPdf} className="rounded-xl border px-4 py-2 text-sm font-bold disabled:opacity-50">Export PDF</button>}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Subscription ID</label>
          <input
            list="responseops-playbook-discovery-subscriptions"
            value={target.subscriptionId}
            onChange={(e) => setTarget({ ...target, subscriptionId: e.target.value })}
            className="w-full rounded-xl border bg-background px-3 py-2 text-sm font-mono"
            placeholder="subscription id"
          />
          <datalist id="responseops-playbook-discovery-subscriptions">{subscriptions.map((s) => <option key={s} value={s} />)}</datalist>
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Default resource group / context</label>
          <input
            list="responseops-playbook-discovery-resourcegroups"
            value={target.resourceGroup}
            onChange={(e) => setTarget({ ...target, resourceGroup: e.target.value })}
            className="w-full rounded-xl border bg-background px-3 py-2 text-sm"
            placeholder="optional context resource group"
          />
          <datalist id="responseops-playbook-discovery-resourcegroups">{resourceGroups.map((g) => <option key={g} value={g} />)}</datalist>
        </div>
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1">Region</label>
          <select value={target.location} onChange={(e) => setTarget({ ...target, location: e.target.value })} className="w-full rounded-xl border bg-background px-3 py-2 text-sm">
            {(regions.length ? regions : ['eastus']).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      {message && <div className="mt-4 rounded-xl border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-700 dark:text-blue-300">{message}</div>}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Metric label="Playbooks" value={items.length} />
        <Metric label="Resource Groups" value={byRg.size} />
        <Metric label="Enabled" value={items.filter((i) => /enabled/i.test(String(i.state))).length} />
        <Metric label="AI Descriptions" value={items.filter((i) => i.descriptionSource === 'ai').length} />
      </div>
    </div>

    <div className="grid grid-cols-1 xl:grid-cols-[460px,1fr] gap-5">
      <div className="rounded-2xl border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <h4 className="font-bold">Discovered Logic Apps</h4>
          <span className="text-xs text-muted-foreground">{filtered.length}/{items.length}</span>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, RG, connector, description, resource ID…" className="w-full rounded-xl border bg-background px-3 py-2 text-sm" />
        <div className="max-h-[680px] overflow-auto space-y-2">
          {filtered.map((item) => <button key={item.resourceId} onClick={() => setSelected(item)} className={`w-full text-left rounded-xl border p-3 hover:bg-muted ${active?.resourceId === item.resourceId ? 'border-primary bg-primary/5' : ''}`}>
            <div className="font-semibold text-sm truncate">{item.workflowName}</div>
            <div className="mt-1 flex flex-wrap gap-1.5 text-[11px]">
              <span className={`px-2 py-0.5 rounded-full border ${badge(item.state)}`}>{item.state || 'Unknown'}</span>
              <span className={`px-2 py-0.5 rounded-full border ${badge(item.triggerType)}`}>{item.triggerType || 'trigger unknown'}</span>
              <span className="px-2 py-0.5 rounded-full border border-border bg-muted text-muted-foreground">{item.resourceGroup || '-'}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{item.aiDescription || item.description || '-'}</div>
          </button>)}
          {!filtered.length && <div className="rounded-xl border p-4 text-sm text-muted-foreground">No playbooks loaded. Run discovery first, or adjust the search filter.</div>}
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-5 space-y-4">
        {active ? <>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
            <div>
              <h3 className="text-xl font-bold break-all">{active.workflowName}</h3>
              <p className="text-xs text-muted-foreground break-all mt-1">{active.resourceId}</p>
            </div>
            <span className={`px-2 py-1 rounded-lg border text-xs font-bold h-fit ${badge(active.state)}`}>{active.state || 'Unknown'}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Info label="Subscription" value={active.subscriptionId} mono />
            <Info label="Resource Group" value={active.resourceGroup} />
            <Info label="Location" value={active.location || '-'} />
            <Info label="Trigger Type" value={active.triggerType || '-'} />
            <Info label="Trigger Name" value={active.triggerName || '-'} />
            <Info label="Last Updated" value={formatDate(active.updatedAt)} />
          </div>
          <div className="rounded-xl border bg-muted/20 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">AI generated description</div>
            <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">{active.aiDescription || active.description || '-'}</p>
            <div className="mt-2 text-[11px] text-muted-foreground">Source: {active.descriptionSource || 'fallback'}</div>
          </div>
          <div>
            <div className="text-sm font-bold mb-2">Connectors</div>
            <div className="flex flex-wrap gap-2">{(active.connectors ?? []).length ? active.connectors?.map((c) => <span key={c} className="px-2 py-1 rounded-full border bg-muted text-xs">{c}</span>) : <span className="text-sm text-muted-foreground">No managed connector detected in discovery payload.</span>}</div>
          </div>
          <div>
            <div className="text-sm font-bold mb-2">Workflow actions preview</div>
            <div className="space-y-2 max-h-[360px] overflow-auto">{(active.actionsSummary ?? []).map((a, index) => <div key={`${a.title}-${index}`} className="rounded-xl border p-3">
              <div className="flex justify-between gap-2"><div className="font-semibold text-sm">{index + 1}. {a.title || a.type || 'Action'}</div><div className="flex gap-1.5">{a.risk && <span className={`px-2 py-0.5 rounded-full border text-[11px] ${badge(a.risk)}`}>{a.risk}</span>}{a.approvalRequired && <span className="px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-700 dark:text-amber-300">Approval</span>}</div></div>
              <div className="text-xs text-muted-foreground mt-1">{a.purpose || '-'}</div>
              {(a.type || a.connector) && <div className="text-[11px] text-muted-foreground mt-2">Type: {a.type || '-'} · Connector: {a.connector || '-'}</div>}
            </div>)}{!(active.actionsSummary ?? []).length && <div className="rounded-xl border p-4 text-sm text-muted-foreground">No action-level preview available.</div>}</div>
          </div>
          <details className="rounded-xl border bg-muted/20 p-3">
            <summary className="cursor-pointer text-sm font-bold">CSV preview for current filter</summary>
            <pre className="mt-3 max-h-[240px] overflow-auto text-xs">{csvPreview}</pre>
          </details>
        </> : <div className="text-muted-foreground">Select a discovered playbook to view details.</div>}
      </div>
    </div>
  </div>;
}

function Metric({ label, value }: { label: string; value: number }) {
  return <div className="rounded-xl border bg-muted/20 p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="text-2xl font-bold">{value}</div></div>;
}
function Info({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return <div className="rounded-xl border bg-muted/20 p-3"><div className="text-xs text-muted-foreground">{label}</div><div className={`text-sm font-semibold break-all ${mono ? 'font-mono' : ''}`}>{value || '-'}</div></div>;
}
